import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  Command, 
  X, 
  BarChart3, 
  FileText, 
  Building, 
  ClipboardList, 
  Camera, 
  Sparkles, 
  Activity, 
  Languages, 
  GraduationCap, 
  Brain, 
  BookOpen, 
  Video, 
  TrendingUp, 
  Calculator, 
  DollarSign, 
  HelpCircle,
  Shield,
  Settings,
  Users,
  AlertTriangle,
  CheckCircle,
  Clock,
  Wrench,
  Lightbulb,
  MessageSquare,
  Award,
  Target,
  Eye
} from 'lucide-react';
import { Input } from './ui/input';
import { Badge } from './ui/badge';

// Define all searchable items with keywords for better search matching
const searchableItems = [
  // Main Pages
  { 
    name: 'Dashboard', 
    href: '/dashboard', 
    icon: BarChart3, 
    category: 'Pages',
    keywords: ['home', 'overview', 'main', 'stats', 'analytics', 'summary'],
    description: 'Main dashboard with safety metrics'
  },
  { 
    name: 'Safety Observations', 
    href: '/observations', 
    icon: Eye, 
    category: 'Pages',
    keywords: ['observation', 'capture', 'photo', 'video', 'voice', 'record', 'hazard', 'finding'],
    description: 'Capture and track safety observations'
  },
  { 
    name: 'Audits', 
    href: '/audits', 
    icon: FileText, 
    category: 'Pages',
    keywords: ['inspection', 'audit', 'compliance', 'osha', 'check', 'assessment'],
    description: 'Safety audits and inspections'
  },
  { 
    name: 'Companies', 
    href: '/companies', 
    icon: Building, 
    category: 'Pages',
    keywords: ['company', 'organization', 'business', 'client', 'site', 'location'],
    description: 'Manage companies and locations'
  },
  { 
    name: 'Safety Work Management', 
    href: '/safety-work', 
    icon: ClipboardList, 
    category: 'Pages',
    keywords: ['task', 'project', 'kanban', 'gantt', 'work', 'management', 'board'],
    description: 'Tasks, projects and work management'
  },
  { 
    name: 'AI Safety Analysis', 
    href: '/safety-analysis', 
    icon: Camera, 
    category: 'AI Tools',
    keywords: ['ai', 'analysis', 'image', 'photo', 'scan', 'identify', 'hazard', 'detect'],
    description: 'AI-powered safety image analysis'
  },
  { 
    name: 'Advanced AI Tools', 
    href: '/advanced-tools', 
    icon: Sparkles, 
    category: 'AI Tools',
    keywords: ['ai', 'advanced', 'tools', 'generator', 'assistant', 'smart'],
    description: 'Advanced AI safety tools'
  },
  { 
    name: 'Predictive Analytics', 
    href: '/predictive', 
    icon: Activity, 
    category: 'Analytics',
    keywords: ['predict', 'forecast', 'analytics', 'risk', 'trend', 'future'],
    description: 'Predictive safety analytics'
  },
  { 
    name: 'Translation Hub', 
    href: '/translation', 
    icon: Languages, 
    category: 'Tools',
    keywords: ['translate', 'language', 'spanish', 'multilingual', 'document'],
    description: 'Translate safety documents'
  },
  { 
    name: 'Training Center', 
    href: '/training', 
    icon: GraduationCap, 
    category: 'Training',
    keywords: ['train', 'course', 'learning', 'vr', 'education', 'certification', 'module'],
    description: 'OSHA safety training courses'
  },
  { 
    name: 'Knowledge Center', 
    href: '/knowledge-center', 
    icon: Brain, 
    category: 'Resources',
    keywords: ['knowledge', 'learn', 'guide', 'resource', 'information', 'database'],
    description: 'Safety knowledge base'
  },
  { 
    name: 'OSHA Regulations', 
    href: '/osha-regulations', 
    icon: BookOpen, 
    category: 'Resources',
    keywords: ['osha', 'regulation', 'standard', 'law', 'compliance', 'requirement', 'code'],
    description: 'OSHA regulations search'
  },
  { 
    name: 'Live Monitoring', 
    href: '/live-monitoring', 
    icon: Video, 
    category: 'Monitoring',
    keywords: ['live', 'monitor', 'real-time', 'camera', 'stream', 'watch', 'surveillance'],
    description: 'Real-time safety monitoring'
  },
  { 
    name: 'Reports', 
    href: '/reports', 
    icon: TrendingUp, 
    category: 'Reports',
    keywords: ['report', 'export', 'pdf', 'download', 'document', 'summary', 'data'],
    description: 'Generate safety reports'
  },
  { 
    name: 'OSHA Logs', 
    href: '/osha-logs', 
    icon: FileText, 
    category: 'Compliance',
    keywords: ['log', 'osha', '300', '301', 'record', 'incident', 'injury', 'illness'],
    description: 'OSHA 300/301 logs management'
  },
  { 
    name: 'ROI Dashboard', 
    href: '/roi', 
    icon: Calculator, 
    category: 'Analytics',
    keywords: ['roi', 'return', 'investment', 'cost', 'savings', 'value', 'money'],
    description: 'Safety ROI calculator'
  },
  { 
    name: 'Billing', 
    href: '/billing', 
    icon: DollarSign, 
    category: 'Account',
    keywords: ['billing', 'payment', 'subscription', 'invoice', 'plan', 'upgrade'],
    description: 'Billing and subscription'
  },
  { 
    name: 'Help Center', 
    href: '/help', 
    icon: BookOpen, 
    category: 'Support',
    keywords: ['help', 'guide', 'how to', 'tutorial', 'faq', 'question'],
    description: 'Help guides and tutorials'
  },
  { 
    name: 'Support', 
    href: '/support', 
    icon: HelpCircle, 
    category: 'Support',
    keywords: ['support', 'ticket', 'contact', 'help', 'issue', 'problem'],
    description: 'Contact support team'
  },
  // Quick Actions
  { 
    name: 'Create New Audit', 
    href: '/audits', 
    icon: FileText, 
    category: 'Quick Actions',
    keywords: ['new', 'create', 'audit', 'start', 'begin', 'add'],
    description: 'Start a new safety audit',
    action: 'create-audit'
  },
  { 
    name: 'Analyze Image', 
    href: '/safety-analysis', 
    icon: Camera, 
    category: 'Quick Actions',
    keywords: ['analyze', 'image', 'photo', 'scan', 'upload', 'check'],
    description: 'Analyze an image for hazards',
    action: 'analyze-image'
  },
  { 
    name: 'Generate Report', 
    href: '/reports', 
    icon: TrendingUp, 
    category: 'Quick Actions',
    keywords: ['generate', 'report', 'create', 'export', 'download'],
    description: 'Generate a safety report',
    action: 'generate-report'
  },
  { 
    name: 'Add Company', 
    href: '/companies', 
    icon: Building, 
    category: 'Quick Actions',
    keywords: ['add', 'company', 'new', 'create', 'organization'],
    description: 'Add a new company',
    action: 'add-company'
  },
  // Features
  { 
    name: 'Toolbox Talks', 
    href: '/training', 
    icon: MessageSquare, 
    category: 'Features',
    keywords: ['toolbox', 'talk', 'daily', 'meeting', 'safety', 'discussion'],
    description: 'Daily toolbox talks'
  },
  { 
    name: 'Certifications', 
    href: '/training', 
    icon: Award, 
    category: 'Features',
    keywords: ['certification', 'certificate', 'license', 'credential', 'qualify'],
    description: 'Track certifications'
  },
  { 
    name: 'Hazard Identification', 
    href: '/safety-analysis', 
    icon: AlertTriangle, 
    category: 'Features',
    keywords: ['hazard', 'identify', 'risk', 'danger', 'threat', 'warning'],
    description: 'Identify workplace hazards'
  },
  { 
    name: 'Incident Tracking', 
    href: '/osha-logs', 
    icon: AlertTriangle, 
    category: 'Features',
    keywords: ['incident', 'accident', 'injury', 'track', 'record', 'log'],
    description: 'Track safety incidents'
  },
];

// Category colors for visual distinction
const categoryColors = {
  'Pages': 'bg-blue-100 text-blue-700',
  'AI Tools': 'bg-purple-100 text-purple-700',
  'Analytics': 'bg-green-100 text-green-700',
  'Tools': 'bg-amber-100 text-amber-700',
  'Training': 'bg-orange-100 text-orange-700',
  'Resources': 'bg-cyan-100 text-cyan-700',
  'Monitoring': 'bg-red-100 text-red-700',
  'Reports': 'bg-emerald-100 text-emerald-700',
  'Compliance': 'bg-indigo-100 text-indigo-700',
  'Account': 'bg-gray-100 text-gray-700',
  'Support': 'bg-teal-100 text-teal-700',
  'Quick Actions': 'bg-rose-100 text-rose-700',
  'Features': 'bg-violet-100 text-violet-700',
};

const GlobalSearch = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Handle keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(true);
      }
      if (e.key === 'Escape') {
        setIsOpen(false);
        setQuery('');
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Search logic
  useEffect(() => {
    const performSearch = () => {
      if (!query.trim()) {
        // Show popular/recent items when no query
        return searchableItems.slice(0, 8);
      }

      const searchTerms = query.toLowerCase().split(' ');
      
      const filtered = searchableItems
        .map(item => {
          let score = 0;
          const itemName = item.name.toLowerCase();
          const itemKeywords = item.keywords.join(' ').toLowerCase();
          const itemDescription = item.description.toLowerCase();

          searchTerms.forEach(term => {
            // Exact name match (highest priority)
            if (itemName === term) score += 100;
            // Name starts with term
            else if (itemName.startsWith(term)) score += 50;
            // Name contains term
            else if (itemName.includes(term)) score += 30;
            // Keyword exact match
            if (item.keywords.some(k => k === term)) score += 40;
            // Keyword contains term
            else if (itemKeywords.includes(term)) score += 20;
            // Description contains term
            if (itemDescription.includes(term)) score += 10;
          });

          return { ...item, score };
        })
        .filter(item => item.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 10);

      return filtered;
    };

    const newResults = performSearch();
    setResults(newResults);
    if (query.trim()) {
      setSelectedIndex(0);
    }
  }, [query]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => Math.min(prev + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && results[selectedIndex]) {
      e.preventDefault();
      handleSelect(results[selectedIndex]);
    }
  };

  const handleSelect = (item) => {
    navigate(item.href);
    setIsOpen(false);
    setQuery('');
  };

  // Group results by category
  const groupedResults = results.reduce((acc, item) => {
    if (!acc[item.category]) {
      acc[item.category] = [];
    }
    acc[item.category].push(item);
    return acc;
  }, {});

  return (
    <>
      {/* Search Trigger Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-3 py-2 text-sm text-gray-500 bg-white border border-gray-200 rounded-xl hover:border-gray-300 hover:bg-gray-50 transition-all shadow-sm min-w-[200px]"
      >
        <Search className="w-4 h-4 text-gray-400" />
        <span className="flex-1 text-left">Search...</span>
        <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium text-gray-400 bg-gray-100 rounded">
          <Command className="w-3 h-3" />K
        </kbd>
      </button>

      {/* Search Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => {
              setIsOpen(false);
              setQuery('');
            }}
          />
          
          {/* Modal */}
          <div className="relative min-h-screen flex items-start justify-center pt-[15vh] px-4">
            <div className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Search Input */}
              <div className="flex items-center gap-3 px-4 py-4 border-b">
                <Search className="w-5 h-5 text-gray-400" />
                <input
                  ref={inputRef}
                  type="text"
                  placeholder="Search pages, features, and more..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="flex-1 text-lg outline-none placeholder-gray-400"
                />
                {query && (
                  <button 
                    onClick={() => setQuery('')}
                    className="p-1 hover:bg-gray-100 rounded"
                  >
                    <X className="w-4 h-4 text-gray-400" />
                  </button>
                )}
                <button 
                  onClick={() => {
                    setIsOpen(false);
                    setQuery('');
                  }}
                  className="p-1 hover:bg-gray-100 rounded text-sm text-gray-500"
                >
                  ESC
                </button>
              </div>

              {/* Results */}
              <div className="max-h-[60vh] overflow-y-auto p-2">
                {results.length === 0 ? (
                  <div className="py-8 text-center text-gray-500">
                    <Search className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-lg font-medium">No results found</p>
                    <p className="text-sm">Try different keywords or browse categories</p>
                  </div>
                ) : (
                  Object.entries(groupedResults).map(([category, items]) => (
                    <div key={category} className="mb-4">
                      <div className="px-3 py-2">
                        <Badge className={`${categoryColors[category] || 'bg-gray-100 text-gray-700'} text-xs font-medium`}>
                          {category}
                        </Badge>
                      </div>
                      <div className="space-y-1">
                        {items.map((item, idx) => {
                          const globalIndex = results.indexOf(item);
                          const Icon = item.icon;
                          const isSelected = globalIndex === selectedIndex;
                          
                          return (
                            <button
                              key={`${item.name}-${idx}`}
                              onClick={() => handleSelect(item)}
                              onMouseEnter={() => setSelectedIndex(globalIndex)}
                              className={`w-full flex items-center gap-3 px-3 py-3 rounded-xl text-left transition-all ${
                                isSelected 
                                  ? 'bg-blue-50 border border-blue-200' 
                                  : 'hover:bg-gray-50'
                              }`}
                            >
                              <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                isSelected ? 'bg-blue-100' : 'bg-gray-100'
                              }`}>
                                <Icon className={`w-5 h-5 ${isSelected ? 'text-blue-600' : 'text-gray-600'}`} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`font-medium truncate ${isSelected ? 'text-blue-900' : 'text-gray-900'}`}>
                                  {item.name}
                                </p>
                                <p className="text-sm text-gray-500 truncate">
                                  {item.description}
                                </p>
                              </div>
                              {isSelected && (
                                <span className="text-xs text-blue-500 font-medium">Enter ↵</span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Footer */}
              <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between text-xs text-gray-500">
                <div className="flex items-center gap-4">
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-white border rounded text-[10px]">↑↓</kbd>
                    Navigate
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-white border rounded text-[10px]">↵</kbd>
                    Select
                  </span>
                  <span className="flex items-center gap-1">
                    <kbd className="px-1.5 py-0.5 bg-white border rounded text-[10px]">ESC</kbd>
                    Close
                  </span>
                </div>
                <span className="text-gray-400">
                  Powered by OSHA Audit
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GlobalSearch;
